/**
 * `data-qa-*` test-attribute convention + helper.
 *
 * This is a web-only test affordance: a single, documented way to tag DOM
 * elements with deterministic attributes so the `qa-tester` agent can locate
 * elements, read their state, and assert on values without relying on brittle
 * CSS selectors or text matching. `ui-tests-plan-writer` writes test steps in
 * terms of these ids, so this convention is the contract between those agents.
 *
 * ## The three attribute families (kebab-case, all prefixed `data-qa-`)
 *
 * - `data-qa-id="<stable-element-id>"` — a stable, human-readable identifier
 *   for an element the agent needs to find or click
 *   (e.g. `data-qa-id="login-submit"`, `data-qa-id="chat-message-input"`).
 *   Make it globally unique within a page where practical; for list rows,
 *   suffix with the entity id (e.g. `data-qa-id="chat-message-<messageId>"`).
 * - `data-qa-value="<value>"` — the asserted value/content of an element when
 *   the visible text is not a reliable assertion target (e.g.
 *   `data-qa-value="42"` for a coin balance, `data-qa-value="ready"` for an
 *   upload status). Use when the agent must read state, not just presence.
 * - `data-qa-status="<state>"` — a coarse lifecycle/state token for stateful
 *   widgets (e.g. `data-qa-status="loading" | "ready" | "error"`), so the agent
 *   can `wait_for` a known state token rather than guessing on spinners.
 *
 * ## Rules
 *
 * - These attributes are **additive and test-only** — they never carry business
 *   logic, never affect rendering, and are safe to ship to production (they are
 *   inert `data-*` attributes). They are **not** gated behind an env flag.
 * - Prefer a `data-qa-id` on every element a UI-test plan references by name.
 * - Do not invent ad-hoc attribute names — only the three families above. New
 *   needs extend the convention via a docs change, not a one-off attribute.
 * - **l10n exemption:** `data-qa-*` values are test tokens, not user-visible UI
 *   copy. They are intentionally hardcoded English/identifier strings and are
 *   exempt from localization by design — do not flag them as missing l10n.
 *
 * ## id-naming convention
 *
 * Prefix ids with the feature/area, e.g. `'wallet-card-balance'`,
 * `'settings-toggle-<key>'`; list/grid rows suffix the entity id, e.g.
 * `'chat-message-<id>'`. Keep ids page-unique so two features never collide on
 * a bare id like `'submit'`.
 *
 * ## Usage
 *
 * Spread the returned object onto a JSX element:
 *
 * ```tsx
 * <button {...qaAttr({ id: 'login-submit' })}>Continue</button>
 * <span {...qaAttr({ id: 'coin-balance', value: balance })}>{balance}</span>
 * <div {...qaAttr({ id: 'upload', status: 'ready' })} />
 * ```
 */

export interface QaAttrOptions {
  /** Stable, human-readable element identifier (`data-qa-id`). */
  id: string;
  /** Asserted value/content when visible text is not a reliable target. */
  value?: string | number;
  /** Coarse lifecycle/state token (e.g. `loading` | `ready` | `error`). */
  status?: string;
}

/**
 * Build the `data-qa-*` props object to spread onto a JSX element.
 *
 * Only the keys whose corresponding option is provided are emitted; undefined
 * options produce no key (no empty-string attributes). `value` is stringified.
 *
 * @param options  The element's `id` (`data-qa-id`) plus optional `value`
 *                 (`data-qa-value`) and `status` (`data-qa-status`).
 */
export const qaAttr = (options: QaAttrOptions): Record<string, string> => {
  const attrs: Record<string, string> = { 'data-qa-id': options.id };

  if (options.value !== undefined) {
    attrs['data-qa-value'] = String(options.value);
  }

  if (options.status !== undefined) {
    attrs['data-qa-status'] = options.status;
  }

  return attrs;
};
